/** @jsx jsx */

import { Icon } from '@zapier/zinnia-icons';

import {
  Colors,
  Tag,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from '@zapier/design-system';
import { Spacing } from '@zapier/theme';
import { jsx, css } from '@emotion/core';

import Link from 'app/common/components/Link';
import ServiceIcon from 'app/developer-v3/components/ServiceIcon';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { getZapTemplateEditUrl } from 'app/zap-templates/utils';
import { Status, StatusText, ZapTemplate } from 'app/zap-templates/types';

import ActionsMenu from './ActionsMenu';

const colorMap = {
  [Status.draft]: 'inverted',
  [Status.forReview]: 'BrandPeach',
  [Status.invalid]: 'error',
  [Status.rejected]: 'warning',
  [Status.published]: 'success',
};

const labelMap = {
  [Status.draft]: {
    title: 'Visible only by you.',
    helpText:
      'Submit this Zap template for review when you are ready for it to go public.',
  },
  [Status.forReview]: {
    title: 'Being reviewed by Zapier staff.',
    helpText:
      'Zapier is reviewing this Zap template and will email you with next steps (usually takes 1-2 weeks).',
  },
  [Status.invalid]: {
    title: 'This Zap template is no longer valid.',
    helpText: 'Review that the steps are correct on this Zap template.',
  },
  [Status.rejected]: {
    title: 'Zapier staff rejected the Zap template.',
    helpText:
      'After review, the Zap template needs improvements. Please review the recommendations from the staff.',
  },
  [Status.published]: {
    color: '#13d0ab', // gamma
    title: 'Visible by anyone.',
    helpText:
      'This Zap template has been approved and is visible to all Zapier users.',
  },
};

const Styles = {
  icon: css`
    display: flex;
    flex: 0;
  `,
  root: css`
    border-top: 1px solid;
    border-top-color: ${Colors.GrayWarm4};
    padding: ${Spacing.medium}px;
    :hover {
      background-color: ${Colors.PrimeWhite};
    }
  `,
  services: css`
    align-items: center;
    display: flex;
    flex: 0;
    gap: ${Spacing.small}px;
  `,
  status: css`
    flex: 0;
    position: relative;
  `,
  statusTag: css`
    text-wrap: nowrap;
  `,
  title: css`
    flex: 1;
  `,
  item: css`
    align-items: center;
    display: flex;
    gap: ${Spacing.medium}px;
  `,
  iconAndName: css`
    align-items: center;
    display: flex;
    gap: ${Spacing.medium}px;
    justify-content: space-between;
  `,
  link: css`
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
  `,
};

type Props = {
  zapTemplate: ZapTemplate;
};

function ZapTemplateItem({ zapTemplate }: Props) {
  const url = getZapTemplateEditUrl(zapTemplate.id);

  return (
    <li css={Styles.root}>
      <div css={Styles.item}>
        <Link css={Styles.link} href={url.href} target="_blank">
          <span css={Styles.iconAndName}>
            <ServiceIcon
              isSymbol={true}
              serviceName={zapTemplate.headService.name}
              size={40}
              src={zapTemplate.headService.image32x32}
            />
            <Icon color="GrayWarm6" name="actionPlayFill" size={16} />
            <ServiceIcon
              isSymbol={true}
              serviceName={zapTemplate.tailService.name}
              size={40}
              src={zapTemplate.tailService.image32x32}
            />
            <Text type="Body1">
              {zapTemplate.title || 'Name your Zap template'}
            </Text>
          </span>
          <span css={Styles.status}>
            {labelMap[zapTemplate.status] ? (
              <Tooltip
                allowMultiline={true}
                content={
                  labelMap[zapTemplate.status].title +
                  ' ' +
                  labelMap[zapTemplate.status].helpText
                }
              >
                <TooltipFocusableTrigger>
                  <Tag
                    css={Styles.statusTag}
                    variant={colorMap[zapTemplate.status]}
                  >
                    {StatusText[zapTemplate.status]}
                  </Tag>
                </TooltipFocusableTrigger>
              </Tooltip>
            ) : (
              <Text css={Styles.statusTag}>
                {StatusText[zapTemplate.status] ?? '--'}
              </Text>
            )}
          </span>
        </Link>
        <span css={Styles.icon}>
          <ActionsMenu
            id={zapTemplate.id}
            landingPageUrl={`${ZAPIER_APP_BASE_URL}${zapTemplate.guidedRecipeUrl}`}
            status={zapTemplate.status}
            title={zapTemplate.title}
          />
        </span>
      </div>
    </li>
  );
}

export default ZapTemplateItem;
